import { useEffect, useState } from "react";
import {
  Calculator,
  TrendingUp,
  Layers,
  Users as UsersIcon,
  Activity,
  ArrowRight,
} from "lucide-react";
import { getCalculatorEstimates, getCalculatorStats } from "@/lib/admin-api";
import { Link } from "react-router-dom";

const fmtMxn = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));

const MODEL_LABELS = {
  flex_curve_largo: "Flex Curve Largo",
  flex_curve_corto: "Flex Curve Corto",
  full_curve: "Full Curve",
};

const CHANNEL_LABELS = {
  google_ads: "Google Ads",
  meta_ads: "Meta Ads",
  organico: "Orgánico",
  directo: "Directo",
  otros: "Otros",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Monterrey",
    });
  } catch {
    return iso;
  }
}

export default function AdminCalculadora() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterModel, setFilterModel] = useState("");
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const loadStats = async () => {
    try {
      const data = await getCalculatorStats();
      setStats(data);
    } catch {
      setStats(null);
    }
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filterModel) params.model_key = filterModel;
      const data = await getCalculatorEstimates(params);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterModel]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="p-5 lg:p-7" data-testid="admin-calculator-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
          <Calculator className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-neutral-900">Calculadora</h1>
          <p className="text-sm text-neutral-500">
            Uso del cotizador paramétrico FlexShade Curvo en línea continua
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-7">
        <KpiCard
          icon={<Activity className="w-4 h-4" />}
          label="Estimaciones totales"
          value={(stats?.total ?? 0).toLocaleString()}
          testid="kpi-total"
        />
        <KpiCard
          icon={<UsersIcon className="w-4 h-4" />}
          label="→ Convertidas a lead"
          value={(stats?.conversions_to_lead ?? 0).toLocaleString()}
          testid="kpi-conv"
          accent
        />
        <KpiCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Tasa de conversión"
          value={`${stats?.conversion_rate_pct ?? 0}%`}
          testid="kpi-cvr"
        />
        <KpiCard
          icon={<Layers className="w-4 h-4" />}
          label="Modelo más cotizado"
          value={
            stats?.by_model?.[0]
              ? MODEL_LABELS[stats.by_model[0]._id] || stats.by_model[0]._id
              : "—"
          }
          testid="kpi-top-model"
          small
        />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-2 gap-4 mb-7">
        {/* Por modelo */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500 mb-4">
            Distribución por modelo
          </h2>
          <div className="space-y-3">
            {(stats?.by_model || []).map((m) => {
              const pct = stats?.total ? (m.count / stats.total) * 100 : 0;
              return (
                <div key={m._id} data-testid={`bymodel-${m._id}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-semibold text-neutral-800">
                      {MODEL_LABELS[m._id] || m._id}
                    </span>
                    <span className="tabular-nums text-neutral-500">
                      {m.count} · {fmtMxn(m.avg_total)}
                    </span>
                  </div>
                  <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {(!stats?.by_model || stats.by_model.length === 0) && (
              <div className="text-sm text-neutral-400 italic py-6 text-center">
                Sin datos aún. La calculadora se completará conforme entren
                visitas.
              </div>
            )}
          </div>
        </div>

        {/* Top cajones */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500 mb-4">
            Tamaños más cotizados
          </h2>
          <div className="grid grid-cols-5 gap-2">
            {(stats?.by_cajones || []).slice(0, 10).map((c) => (
              <div
                key={c._id}
                className="bg-neutral-50 border border-neutral-200 rounded-lg p-2 text-center"
                data-testid={`bycajon-${c._id}`}
              >
                <div className="text-xs text-neutral-500 font-medium">
                  {c._id} caj
                </div>
                <div className="text-lg font-black tabular-nums text-neutral-900">
                  {c.count}
                </div>
              </div>
            ))}
            {(!stats?.by_cajones || stats.by_cajones.length === 0) && (
              <div className="col-span-5 text-sm text-neutral-400 italic py-6 text-center">
                Aún sin datos.
              </div>
            )}
          </div>

          {stats?.by_channel?.length > 0 && (
            <>
              <h3 className="mt-5 text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
                Canales
              </h3>
              <div className="flex flex-wrap gap-2">
                {stats.by_channel.map((c) => (
                  <span
                    key={c._id || "na"}
                    className="text-xs px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200"
                    data-testid={`bychannel-${c._id || "na"}`}
                  >
                    {CHANNEL_LABELS[c._id] || c._id || "(sin canal)"} · {c.count}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-200 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-700">
              Últimas estimaciones
            </h2>
            <p className="text-xs text-neutral-500">
              {total.toLocaleString()} registros · página {page} de {totalPages}
            </p>
          </div>
          <select
            value={filterModel}
            onChange={(e) => {
              setFilterModel(e.target.value);
              setPage(1);
            }}
            data-testid="filter-model"
            className="text-sm px-3 py-2 rounded-lg border border-neutral-200 bg-white"
          >
            <option value="">Todos los modelos</option>
            <option value="flex_curve_largo">Flex Curve Largo</option>
            <option value="flex_curve_corto">Flex Curve Corto</option>
            <option value="full_curve">Full Curve</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Fecha</th>
                <th className="text-left px-4 py-3 font-bold">Modelo</th>
                <th className="text-right px-4 py-3 font-bold">Cajones</th>
                <th className="text-right px-4 py-3 font-bold">Ancho (m)</th>
                <th className="text-right px-4 py-3 font-bold">Rango MXN</th>
                <th className="text-left px-4 py-3 font-bold">Canal</th>
                <th className="text-left px-4 py-3 font-bold">Sesión</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-neutral-400"
                  >
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-neutral-400 italic"
                  >
                    Sin estimaciones para los filtros seleccionados.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id} data-testid={`row-${r.id}`}>
                    <td className="px-4 py-3 text-neutral-700 tabular-nums">
                      {fmtDate(r.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-neutral-900">
                        {MODEL_LABELS[r.model_key] || r.model_label || r.model_key}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-orange-600">
                      {r.cajones}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                      {r.ancho_min_m?.toFixed(1)}-{r.ancho_max_m?.toFixed(1)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-neutral-900">
                      {fmtMxn(r.total_aa)} – {fmtMxn(r.total_aaa)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200">
                        {CHANNEL_LABELS[r.channel] || r.channel || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-500">
                      {(r.session_id || "").slice(0, 8) || "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div className="px-5 py-3 border-t border-neutral-200 flex items-center justify-between text-sm">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="page-prev"
          >
            ← Anterior
          </button>
          <span className="text-neutral-500">
            Página <strong>{page}</strong> de <strong>{totalPages}</strong>
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="page-next"
          >
            Siguiente →
          </button>
        </div>
      </div>

      {/* Recientes con CTA */}
      {stats?.recent?.length > 0 && (
        <div className="mt-6 text-xs text-neutral-500 flex items-center gap-2">
          <ArrowRight className="w-3.5 h-3.5" />
          Tip: leads creados con session_id de la calculadora reciben{" "}
          <strong className="text-neutral-700">+10 a +20 puntos</strong> de score
          automáticamente. Búscalos en{" "}
          <Link to="/admin/leads" className="text-orange-600 underline">
            Leads
          </Link>
          .
        </div>
      )}
    </div>
  );
}

const KpiCard = ({ icon, label, value, accent, small, testid }) => (
  <div
    className={`bg-white border rounded-xl p-4 ${
      accent ? "border-orange-200 bg-orange-50/30" : "border-neutral-200"
    }`}
    data-testid={testid}
  >
    <div
      className={`flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold mb-2 ${
        accent ? "text-orange-700" : "text-neutral-500"
      }`}
    >
      {icon}
      {label}
    </div>
    <div
      className={`font-black tabular-nums text-neutral-900 ${
        small ? "text-base" : "text-2xl md:text-3xl"
      }`}
    >
      {value}
    </div>
  </div>
);
